import { forwardRef, ReactNode } from 'react';
import { DayPicker, DayPickerProps } from 'react-day-picker';
import format from 'date-fns/format/index.js';
import './style.css';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { Button, ButtonProps } from '../Button';
import { useControlledState } from '../../hooks/useControlledState';
import { Icon } from '../Icon';

export interface DatePickerProps
  extends Omit<ButtonProps, 'value' | 'defaultValue' | 'onChange'> {
  as?: React.ElementType;
  value?: Date;
  defaultValue?: Date;
  onChange?: (date?: Date) => void;
  required?: boolean;
  captionLayout?: DayPickerProps['captionLayout'];
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  format?: string;
  error?: string;
  readOnly?: boolean;
  placeholder?: string;
  defaultMonth?: Date;
}

export const DatePicker = forwardRef<HTMLButtonElement, DatePickerProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      format: dateFormat = 'PP',
      leftIcon,
      rightIcon = <Icon inherit name='calendar' size={16} />,
      css,
      required = false,
      captionLayout = 'dropdown',
      placeholder = 'Select Date',
      defaultMonth,
      error,
      readOnly,
      ...rest
    },
    ref,
  ) => {
    const [date, setDate] = useControlledState<Date | undefined>(
      value,
      defaultValue,
      onChange,
    );

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            data-has-value={!!date}
            variant='input'
            data-invalid={!!error}
            aria-invalid={!!error}
            data-readonly={readOnly}
            css={{
              position: 'relative',
              gap: 8,
              ...css,
            }}
            {...rest}
          >
            {leftIcon}
            <span style={{ flex: 1 }}>
              {date ? format(date, dateFormat) : placeholder}
            </span>
            {rightIcon}
          </Button>
        </PopoverTrigger>
        <PopoverContent arrow={false} align='start'>
          <DayPicker
            mode='single'
            captionLayout={captionLayout}
            styles={{
              root: {
                fontFamily: 'Inter, sans-serif',
              },
            }}
            defaultMonth={defaultMonth ?? date}
            required={required}
            selected={date}
            onSelect={setDate}
          />
        </PopoverContent>
      </Popover>
    );
  },
);

DatePicker.displayName = 'DatePicker';
